/**
 * /dev/activity-test — manual + automated harness for the navigator + store.
 *
 * Exercises useActivityNavigator end-to-end through real React Router
 * navigation, validating that:
 *   - open() / push() / replaceEntry() / replaceActivity() / close() all
 *     mutate the store AND change the URL
 *   - usePopstateSync correctly reconciles when the user uses browser back
 *     or this page calls navigate() outside the navigator
 *
 * Visible only in dev (route registered without auth guard, kind="dev").
 * Real user pages will not be migrated to the navigator until Phase 2D
 * (after the ActivityHost rendering decision is made).
 */

import { useNavigate } from "react-router-dom";
import { useActivityStore } from "../store/activityStore";
import { useActivityNavigator } from "../navigation/useActivityNavigator";
import { useRequireAuth } from "../navigation/useRequireAuth";
import { useAuthStore } from "../store/authStore";

const sampleOpen = {
  kind: "dev-test",
  presentation: "overlay" as const,
  appBarAction: "back" as const,
  url: "/dev/activity-test/sub-a",
  fallbackPath: "/dev/activity-test",
};

export default function ActivityTestPage() {
  const navigator = useActivityNavigator();
  const stack = useActivityStore((s) => s.activities);
  const dialogs = useActivityStore((s) => s.dialogs);
  const openDialog = useActivityStore((s) => s.openDialog);
  const closeDialog = useActivityStore((s) => s.closeDialog);
  const reset = useActivityStore((s) => s.reset);
  const navigate = useNavigate();

  return (
    <div style={{ padding: 16, fontFamily: "system-ui" }}>
      <h1 style={{ fontSize: 18, marginBottom: 8 }}>
        Navigator + Store Test (dev only)
      </h1>
      <p style={{ fontSize: 12, color: "#666" }}>
        Phase 2C harness. Open browser devtools → check ActivityStack debug
        dock at bottom-right.
      </p>

      <section style={{ marginTop: 16 }}>
        <h2 style={{ fontSize: 14 }}>Navigator ops</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
          <button
            data-testid="op-open"
            onClick={() => navigator.open(sampleOpen)}
          >
            open(/sub-a)
          </button>
          <button
            data-testid="op-push"
            onClick={() =>
              navigator.push({ url: "/dev/activity-test/sub-b" })
            }
          >
            push(/sub-b)
          </button>
          <button
            data-testid="op-replace-entry"
            onClick={() =>
              navigator.replaceEntry({ url: "/dev/activity-test/sub-c" })
            }
          >
            replaceEntry(/sub-c)
          </button>
          <button
            data-testid="op-replace-activity"
            onClick={() =>
              navigator.replaceActivity({
                ...sampleOpen,
                kind: "dev-test-2",
                url: "/dev/activity-test/sub-d",
              })
            }
          >
            replaceActivity(/sub-d)
          </button>
          <button data-testid="op-close" onClick={() => navigator.close()}>
            close()
          </button>
        </div>
      </section>

      <section style={{ marginTop: 16 }}>
        <h2 style={{ fontSize: 14 }}>Out-of-band navigation</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
          <button
            data-testid="op-browser-back"
            onClick={() => navigate(-1)}
          >
            navigate(-1) (browser back)
          </button>
          <button
            data-testid="op-direct-navigate"
            onClick={() =>
              navigate("/dev/activity-test", { replace: true })
            }
          >
            navigate(/dev/activity-test, replace) (no nav meta)
          </button>
        </div>
      </section>

      <section style={{ marginTop: 16 }}>
        <h2 style={{ fontSize: 14 }}>Auth gate (Phase 3)</h2>
        <RequireAuthSampler />
      </section>

      <section style={{ marginTop: 16 }}>
        <h2 style={{ fontSize: 14 }}>Dialog</h2>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button
            data-testid="op-open-dialog"
            onClick={() => openDialog("test-confirm")}
          >
            openDialog
          </button>
          <button data-testid="op-close-dialog" onClick={() => closeDialog()}>
            closeDialog
          </button>
        </div>
      </section>

      <section style={{ marginTop: 16 }}>
        <h2 style={{ fontSize: 14 }}>State</h2>
        <pre
          data-testid="state-dump"
          style={{
            fontSize: 11,
            background: "#f5f5f5",
            padding: 8,
            borderRadius: "var(--radius-inner)",
            maxHeight: 240,
            overflow: "auto",
          }}
        >
          {JSON.stringify({ activities: stack, dialogs }, null, 2)}
        </pre>
        <button
          data-testid="op-reset"
          onClick={() => reset()}
          style={{ marginTop: 8 }}
        >
          reset store
        </button>
      </section>
    </div>
  );
}

function RequireAuthSampler() {
  const { requireAuth } = useRequireAuth();
  const isAuthed = useAuthStore((s) => !!s.accessToken);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
      <span style={{ fontSize: 11, color: isAuthed ? "#0a0" : "#a00" }}>
        auth: {isAuthed ? "logged in" : "not logged in"}
      </span>
      <button
        data-testid="op-require-auth-navigate"
        onClick={() =>
          requireAuth({
            type: "navigate",
            to: "/dev/activity-test/sub-a",
            mode: "openActivity",
          })
        }
      >
        requireAuth → openActivity(/sub-a)
      </button>
      <button
        data-testid="op-require-auth-callback"
        onClick={() =>
          requireAuth(
            { type: "callback", actionId: "demo-callback" },
            () => alert("callback ran! (only when authed)"),
          )
        }
      >
        requireAuth → callback
      </button>
    </div>
  );
}

export function ActivityTestSubPage({ name }: { name: string }) {
  const navigator = useActivityNavigator();
  const stack = useActivityStore((s) => s.activities);
  return (
    <div style={{ padding: 16, fontFamily: "system-ui" }}>
      <h1 style={{ fontSize: 18 }}>Sub page: {name}</h1>
      <p style={{ fontSize: 12, color: "#666" }}>
        Stack depth: {stack.length}. Top entries:{" "}
        {stack[stack.length - 1]?.entries.length ?? 0}.
      </p>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          data-testid="sub-push"
          onClick={() =>
            navigator.push({ url: `/dev/activity-test/sub-${name}-deeper` })
          }
        >
          push deeper
        </button>
        <button data-testid="sub-close" onClick={() => navigator.close()}>
          close()
        </button>
      </div>
    </div>
  );
}
